<template>
  <div v-if="show" id="annotationToolRibbon" class="ribbon">
    <div class="ribbon__group">
      <IconButton
        id="undoBtn"
        v-tooltip="'Undo'"
        class="ribbon__button"
        aria-label="Undo"
        :disabled="!canUndo"
        :icon="'undo'"
        :showHover="false"
        :type="''"
        @click="handleUndo"
      />
      <IconButton
        id="revertChangesBtn"
        v-tooltip="'Revert All Changes'"
        class="ribbon__button"
        aria-label="Revert All Changes"
        :icon="'restore'"
        :showHover="false"
        :type="''"
        @click="handleRevertChanges"
      />
    </div>
    <div class="ribbon__group">
      <IconButton
        v-if="hasSelection"
        id="showAnnotationInfoBtn"
        v-tooltip="'Show Annotation Info'"
        class="ribbon__button"
        aria-label="Show Annotation Info"
        :icon="'auto'"
        :showHover="false"
        :type="''"
        @click="handleShowAnnotationInfo"
      />
      <button
        v-if="hasReviewTask"
        v-tooltip="'Delete Image'"
        class="button button-danger ribbon__button-secondary button-flex"
        @click="handleDeleteImageFromDataset"
      >
        <SVGIcon
          class="me-1"
          :iconName="'delete'"
          :width="'20px'"
          :height="'20px'"
        />
        Delete Image
      </button>
      <button
        v-if="hasReviewTask"
        v-tooltip="'Skip Image (Right Arrow)'"
        class="button button-secondary ribbon__button-secondary"
        @click="handleSkipImage"
      >
        Skip Image
      </button>
      <template v-if="isAuditMode">
        <BasePersistentSplitButton
          v-model="submitType"
          class="ribbon__button-primary"
          title="Submit Annotations (Enter)"
          :options="submitTypes"
          @click="handleReviewSubmit"
        />
      </template>
      <template v-else>
        <button
          v-tooltip="'Submit Annotations (Enter)'"
          class="button button-rounded ribbon__button-primary"
          @click="handleSubmit"
        >
          Save Annotations
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import SVGIcon from '@/components/SVGIcon.vue';
import IconButton from '@/components/IconButton.vue';
import BasePersistentSplitButton from '@/components/BasePersistentSplitButton.vue';
import useTasks from '@/composables/annotationTool/useTasks.js';
import { useEditorStore } from '@/stores/useEditorStore.js';
import { storeToRefs } from 'pinia';

export default {
  name: 'AnnotationToolRibbon',
  components: {
    SVGIcon,
    IconButton,
    BasePersistentSplitButton,
  },
  props: {
    show: {
      type: Boolean,
      default: true,
    },
    canUndo: {
      type: Boolean,
      default: false,
    },
    hasSelection: {
      type: Boolean,
      default: false,
    },
    annotationDisplaySettings: {
      type: Object,
      default: null,
    },
    isAuditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'undo',
    'revert-changes',
    'show-annotation-info',
    'skip',
    'submit',
    'submit-all-annotations',
    'submit-accepted-annotations',
    'bulk-del-ann',
  ],
  setup() {
    const editorStore = useEditorStore();
    const {
      currentSample,
    } = storeToRefs(editorStore);

    const {
      submitType,
      skipTaskImage,
      handleAuditSubmitAllAnnotations,
      handleAuditSubmitAcceptedAnnotations,
      deleteTaskImageFromDataset,
      hasReviewTask,
    } = useTasks();

    return {
      currentSample,
      submitType,
      skipTaskImage,
      handleAuditSubmitAllAnnotations,
      handleAuditSubmitAcceptedAnnotations,
      deleteTaskImageFromDataset,
      hasReviewTask,
    };
  },
  data() {
    return {
      internalAnnotationDisplaySettings: null,
      submitTypes: [
        { title: 'Submit All Annotations', value: 'submit-all-annotations' },
        { title: 'Submit Accepted Annotations', value: 'submit-accepted-annotations' },
      ],
    };
  },
  mounted() {
    if (this.annotationDisplaySettings) {
      this.internalAnnotationDisplaySettings = this.annotationDisplaySettings;
    }
  },
  methods: {
    handleRevertChanges() {
      this.$emit('revert-changes');
    },
    handleUndo() {
      this.$emit('undo');
    },
    handleShowAnnotationInfo() {
      this.$emit('show-annotation-info');
    },
    async handleDeleteImageFromDataset() {
      await this.deleteTaskImageFromDataset(this.currentSample?.imageObj?.id)
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error);
          }
          return resp.result;
        })
        .catch((error) => {
          console.error(error);
          alert(error);
        });
    },
    handleSkipImage() {
      this.$emit('skip');
      this.skipTaskImage();
    },
    handleSubmit() {
      this.$emit('submit');
    },
    async handleReviewSubmit(submitType) {
      if (submitType === 'submit-all-annotations') {
        await this.handleAuditSubmitAllAnnotations();
      } else if (submitType === 'submit-accepted-annotations') {
        await this.handleAuditSubmitAcceptedAnnotations();
      }
    },
    handleEnter() {
      this.$emit(this.submitType);
    },
    handleDeleteAnnotationsBulk() {
      this.$emit('bulk-del-ann');
    },
  },
};
</script>

<style lang="scss" scoped>
.ribbon {
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  min-height: 37px;
  max-height: 37px;
  padding: 2px 16px;
  justify-content: space-between;
  z-index: 10;
  box-shadow: 0 3px 3px -3px gray, 0 10px 10px -10px gray;
  color: var(--ribbon-icons-color);
  background: var(--ribbon-bg);
  border-left: solid 1px var(--ribbon-border);

  &__group {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  &__vr {
    display: flex;
    width: 1px;
    height: 60%;
    margin: 0;
  }

  &__button {
    display: flex;
    min-width: 22px;
    height: 22px;
    color: inherit;
    background: none;
    border: none;
    font-size: 0.875rem;
    line-height: 1rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .05em;
    align-items: center;
    cursor: pointer;

    &:hover:not(:disabled), &.active:not(:disabled) {
      box-shadow: 0 0 0 4px var(--icon-hover-color);
      background-color: var(--icon-hover-color);
      border-radius: 4px;
    }

    &:disabled {
      cursor: default;
      color: var(--color-disabled);
    }
  }

  &__button-primary {
    display: flex;
    height: 28px;
    font-size: 0.875rem;
    line-height: 1rem;
    align-items: center;
  }

  &__button-secondary {
    display: flex;
    height: 28px;
    font-size: 0.875rem;
    line-height: 1rem;
    align-items: center;
  }

  &__labeled_button {
    width: auto;
  }
}

:deep(.filter-button) {
  height: 22px;
}

</style>
